import { Box, IconButton, Tooltip } from "@mui/material";

import {
  FolderOpenIcon,
  ListNumbersIcon,
  PuzzlePieceIcon,
  UserListIcon,
} from "@phosphor-icons/react/dist/ssr";

import useLesson from "@/contexts/useLesson";
import { LunaTabName } from "@/types";

interface Props {
  activeTab: LunaTabName;
  setActiveTab: (tab: LunaTabName) => void;
  isTutor?: boolean;
}

const LunaTabs = ({ activeTab, setActiveTab, isTutor = false }: Props) => {
  const { lessonResourceState, lessonInteractionProfile } = useLesson();
  const getTabBadge = (tab: LunaTabName) => {
    switch (tab) {
      case LunaTabName.Problems:
        if (isTutor) {
          if (
            lessonInteractionProfile?.realtimeResources?.problems?.some(
              (problem) => !problem.isApproved
            )
          ) {
            return true;
          }
        } else {
          if (
            lessonInteractionProfile?.realtimeResources?.problems?.some(
              (problem) => !problem.studentAnswer && problem.isApproved
            )
          ) {
            return true;
          }
        }
        break;
      case LunaTabName.Examples:
        if (isTutor) {
          if (
            lessonInteractionProfile?.realtimeResources?.examples?.some(
              (example) => !example.isApproved
            )
          ) {
            return true;
          }
        } else {
          const readItems = lessonResourceState?.readItems || [];
          const examples = lessonInteractionProfile?.realtimeResources?.examples?.filter(
            (example) => example.isApproved && !readItems.includes(example.id)
          );
          return !!examples?.length;
        }
        break;
      default:
        return false;
    }
  };
  const tabs = [
    {
      name: LunaTabName.Problems,
      icon: <PuzzlePieceIcon size={16} weight="fill" />,
      tooltip: isTutor ? "Draft and assign practice problems with Luna" : "Your practice problems",
    },
    // {
    //   name: LunaTabName.Solutions,
    //   icon: <HiOutlineLightBulb className="w-full h-full" />,
    // },
    {
      name: LunaTabName.Examples,
      icon: <FolderOpenIcon size={16} weight="bold" />,
      tooltip: isTutor ? "Draft and share concept examples with Luna" : "Your concept examples",
    },
    // {
    //   name: LunaTabName.Diagrams,
    //   icon: <PiChartDonut className="w-full h-full" />,
    // },

    ...(isTutor
      ? [
        {
          name: LunaTabName.StudentProfile,
          icon: <UserListIcon size={16} weight="bold" />,
          tooltip: "Student profile & insights",
        },
        {
          name: LunaTabName.LessonPlan,
          icon: <ListNumbersIcon size={16} weight="bold" />,
          tooltip: "Interactive lesson plan",
        },
      ]
      : []),
  ];
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: 1,
        width: "100%",
        pt: 1.5,
        pb: 1,
      }}
    >
      {tabs.map((tab) => (
        <Box key={tab.name}>
          <Tooltip title={tab.tooltip} arrow>
            <IconButton
              type="button"
              onClick={() => setActiveTab(tab.name)}
              sx={{
                p: 0.75,
                border: "1px solid",
                borderColor:
                  activeTab === tab.name ? "text.primary" : "secondary.main",
                borderRadius: 1.5,
                color: "text.primary",
                backgroundColor:
                  activeTab === tab.name ? "action.selected" : "transparent",
                position: "relative",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              {tab.icon}
              {getTabBadge(tab.name) && (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    position: "absolute",
                    top: 4,
                    right: 4,
                    backgroundColor: "#14532D",
                    border: "1.5px solid #F8F5EC",
                    boxSizing: "border-box",
                  }}
                />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </Box>
  );
};

export default LunaTabs;
